import type { ReportSentryErrorOptions } from '@/services/sentry';

export type RuntimeErrorKind = 'network' | 'chunk' | 'server' | 'client' | 'unknown';
export type ErrorPageDisplayType = '404' | '500' | 'network';
export type ErrorPageAction = 'retry' | 'home';
export type ErrorRenderMode = 'ssr' | 'csr';

interface ErrorPageDisplayModel {
  code: string;
  copyNamespace: 'error' | 'notFound';
  illustration: string;
  showRecommendations: boolean;
  actions: ErrorPageAction[];
}

interface RouteErrorReportInput {
  error: Error & { digest?: string };
  pathname?: string | null;
  online?: boolean;
}

const PATH_SEGMENT_EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;
const PATH_SEGMENT_PHONE_PATTERN = /^\+?\d{7,}$/;
const PATH_SEGMENT_UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const PATH_SEGMENT_LONG_TOKEN_PATTERN = /^(?=.*\d)[a-z0-9_-]{12,}$/i;
const PATH_SEGMENT_NUMERIC_PATTERN = /^\d+$/;

const STATIC_ERROR_PAGE_ROUTES = new Set([
  '/campaigns/bestsellers',
  '/campaigns/flash-sale',
  '/campaigns/french-summer-edit-glasses',
  '/campaigns/spring-sale',
  '/campaigns/sunglasses-sale',
  '/campaigns/zeelool-x-ulike',
  '/blog',
  '/blog/authors',
  '/blog/search-results',
  '/blog/tags',
]);

const DYNAMIC_ERROR_PAGE_ROUTES: Array<{ pattern: RegExp; template: string }> = [
  { pattern: /^\/campaigns\/[^/]+$/, template: '/campaigns/[slug]' },
  { pattern: /^\/trends\/[^/]+$/, template: '/trends/[id]' },
  { pattern: /^\/ambassador-detail\/[^/]+$/, template: '/ambassador-detail/[id]' },
  {
    pattern: /^\/ambassador-work-detail\/[^/]+$/,
    template: '/ambassador-work-detail/[id]',
  },
  { pattern: /^\/question-break\/[^/]+$/, template: '/question-break/[id]' },
  { pattern: /^\/question-complete\/[^/]+$/, template: '/question-complete/[id]' },
  {
    pattern: /^\/account\/my-orders\/order-detail\/[^/]+$/,
    template: '/account/my-orders/order-detail/[id]',
  },
  {
    pattern: /^\/account\/points-mall-detail\/[^/]+$/,
    template: '/account/points-mall-detail/[id]',
  },
  {
    pattern: /^\/account\/transaction-detail\/[^/]+$/,
    template: '/account/transaction-detail/[id]',
  },
  { pattern: /^\/goods-list\/[^/]+$/, template: '/goods-list/[id]' },
  { pattern: /^\/product-list\/[^/]+$/, template: '/product-list/[id]' },
  { pattern: /^\/blog\/authors\/[^/]+$/, template: '/blog/authors/[name]' },
  { pattern: /^\/blog\/tags\/[^/]+$/, template: '/blog/tags/[tag]' },
  { pattern: /^\/blog\/[^/]+\/[^/]+$/, template: '/blog/[type]/[slug]' },
  { pattern: /^\/blog\/[^/]+$/, template: '/blog/[type]' },
];

const ERROR_PAGE_DISPLAY_MODELS: Record<'404' | '500', ErrorPageDisplayModel> = {
  404: {
    code: '404',
    copyNamespace: 'notFound',
    illustration: '/error/error-404.svg',
    showRecommendations: true,
    actions: ['home'],
  },
  500: {
    code: '',
    copyNamespace: 'error',
    illustration: '/error/error-500.svg',
    showRecommendations: true,
    actions: ['retry', 'home'],
  },
};

export function getErrorRenderMode(error: Error & { digest?: string }): ErrorRenderMode {
  return error.digest ? 'ssr' : 'csr';
}

export function getErrorPageDisplayModel(type: ErrorPageDisplayType) {
  return type === '404' ? ERROR_PAGE_DISPLAY_MODELS[404] : ERROR_PAGE_DISPLAY_MODELS[500];
}

function toErrorMessage(error: unknown) {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return '';
}

export function getRuntimeErrorKind(error: unknown, online = true): RuntimeErrorKind {
  const message = toErrorMessage(error).toLowerCase();

  if (!online) {
    return 'network';
  }

  if (/chunk|loading css chunk|dynamic import|importing a module script/i.test(message)) {
    return 'chunk';
  }

  if (/network|failed to fetch|load failed|timeout|timed out|econn/i.test(message)) {
    return 'network';
  }

  if (
    /internal server error|server component|status\s*500|response status code 500/i.test(message)
  ) {
    return 'server';
  }

  if (/hydration|render|client/i.test(message)) {
    return 'client';
  }

  return 'unknown';
}

function normalizePathSegment(segment: string) {
  if (
    PATH_SEGMENT_EMAIL_PATTERN.test(segment) ||
    PATH_SEGMENT_PHONE_PATTERN.test(segment) ||
    PATH_SEGMENT_UUID_PATTERN.test(segment) ||
    PATH_SEGMENT_LONG_TOKEN_PATTERN.test(segment) ||
    PATH_SEGMENT_NUMERIC_PATTERN.test(segment)
  ) {
    return '[id]';
  }

  return segment;
}

function safeDecodePathSegment(segment: string) {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

function hasValidPathEncoding(pathname: string) {
  try {
    decodeURI(pathname);
    return true;
  } catch {
    return false;
  }
}

function getDynamicErrorPageRoute(pathname: string) {
  if (STATIC_ERROR_PAGE_ROUTES.has(pathname)) return pathname;

  return DYNAMIC_ERROR_PAGE_ROUTES.find(({ pattern }) => pattern.test(pathname))?.template;
}

export function normalizeErrorPagePath(pathname?: string | null) {
  if (!pathname) return 'unknown';

  const [pathWithoutQuery] = pathname.split(/[?#]/);
  const normalizedPath = pathWithoutQuery || '/';

  if (normalizedPath.startsWith('/goods-detail/')) {
    return '/goods-detail/[slug]';
  }

  const decodedSegments = normalizedPath.split('/').filter(Boolean).map(safeDecodePathSegment);
  const decodedPath = decodedSegments.length ? `/${decodedSegments.join('/')}` : '/';
  const dynamicRoute = hasValidPathEncoding(normalizedPath)
    ? getDynamicErrorPageRoute(decodedPath)
    : undefined;

  if (dynamicRoute) return dynamicRoute;

  const segments = decodedSegments.map(normalizePathSegment);

  return segments.length ? `/${segments.join('/')}` : '/';
}

export function buildRouteErrorReportOptions({
  error,
  pathname,
  online = true,
}: RouteErrorReportInput): ReportSentryErrorOptions {
  const page = normalizeErrorPagePath(pathname);
  const kind = getRuntimeErrorKind(error, online);

  return {
    domain: 'runtime',
    stage: 'route_error_boundary',
    tags: {
      'error.page': page,
      'error.status_code': 500,
      'error.kind': kind,
      'error.source': 'frontend_runtime',
      monitoring_priority: 'frontend_500',
      'render.mode': getErrorRenderMode(error),
    },
    extra: {
      digest: error.digest,
      online,
      source_path: page,
    },
  };
}

export function extractRecommendProducts(response: unknown) {
  const data = (response as { data?: unknown } | null)?.data;
  const list = (data as { list?: unknown } | null)?.list;
  const frameList = (data as { frame_list?: { list?: unknown } } | null)?.frame_list?.list;

  if (Array.isArray(list)) return list;
  if (Array.isArray(frameList)) return frameList;

  return [];
}
